<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import { Feature, PageName } from '@/api/tracking';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import BasicEmptyFeed from '@/components/common/basic-empty-feed.vue';
import {
  AdpDocument,
  ArticleMarkSource,
  DocType,
  FeedType,
  ThemeFeedType,
} from '@/types';
import { docTypeDisplayNameMap } from '@/utils/adpDocument';
import { useAdpDocumentOptionMenu } from '@/utils/composables/useAdpDocumentOptionMenu';

interface Props {
  title: string;
  displayFeedType: FeedType;
  themeFeedType: ThemeFeedType;
  docType: DocType;
  adpDocuments: AdpDocument[];
  initialFeedCount?: number;
  maxFeedCount?: number;
  pageName: PageName;
  feature: Feature;
  markSource?: ArticleMarkSource;
}

const props = withDefaults(defineProps<Props>(), {
  initialFeedCount: 20,
  maxFeedCount: 200,
});

const { adpDocuments } = toRefs(props);

const isShowAllButtonVisible = ref(
  adpDocuments.value.length > props.initialFeedCount,
);
const visibleAdpDocuments = computed(() =>
  adpDocuments.value.slice(
    0,
    isShowAllButtonVisible.value ? props.initialFeedCount : props.maxFeedCount,
  ),
);
const loadAllAdpDocuments = () => (isShowAllButtonVisible.value = false);

const noFeedMessage = computed(
  () => `${docTypeDisplayNameMap[props.docType]}はありませんでした。`,
);

const optionMenu = useAdpDocumentOptionMenu({
  pageName: props.pageName,
  feature: props.feature,
});

watch(adpDocuments, (newAdpDocuments: AdpDocument[]) => {
  isShowAllButtonVisible.value =
    newAdpDocuments.length > props.initialFeedCount;
});
</script>

<template>
  <div class="document-list">
    <div class="title c-title-bold--m">
      <div class="title-text">{{ title }}</div>
      <div class="feed-count c-text-regular--s">
        ・ {{ adpDocuments.length }}件
      </div>
    </div>
    <div class="divider"></div>
    <div
      class="document-card-wrapper"
      v-for="(document, index) in visibleAdpDocuments"
      :key="document.id"
    >
      <AdpDocumentCard
        width="100%"
        :adp-document="document"
        :theme-id="document.theme_id"
        :page-name="pageName"
        :feature="feature"
        :mark-source="markSource"
        :disable-related-articles="false"
        :option-actions="
          optionMenu.buildOptionActions(document, {
            notInterested: !document.theme_id,
          })
        "
        @option-action="name => optionMenu.handleOptionAction(name, document)"
      ></AdpDocumentCard>
      <div
        v-if="index !== visibleAdpDocuments.length - 1"
        class="divider"
      ></div>
    </div>

    <div v-if="isShowAllButtonVisible" class="view-more-button__wrapper">
      <button
        class="view-more-button c-text-regular--m c-btn c-btn--auto c-btn--small c-btn--outline"
        @click="() => loadAllAdpDocuments()"
      >
        すべて表示{{ `（${adpDocuments.length - initialFeedCount}）` }}
      </button>
    </div>

    <template v-if="adpDocuments.length === 0">
      <BasicEmptyFeed>{{ noFeedMessage }}</BasicEmptyFeed>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.document-list {
  .title {
    display: flex;
    align-items: center;
    padding-left: 16px;

    .title-text {
      padding-top: 10px;
      padding-bottom: 10px;
    }
    .feed-count {
      color: $color-gray600;
    }
  }

  .divider {
    width: auto;
    border-bottom: 0.5px solid $color-gray400;
    margin: 0px 16px 0px 16px;
  }

  .document-card-wrapper {
    margin-bottom: 8px;
    border-radius: 4px;

    .document-card {
      border: 0;
    }
  }

  .view-more-button__wrapper {
    display: flex;
    justify-content: center;
  }
  .view-more-button {
    margin-bottom: 16px;
  }
}
</style>
